// src/layouts/admin/index.tsx
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import { PageHeader } from "components/ui";
import routes, { RoutesType } from "routes";

// 1) Import the EventsProvider
import { EventsProvider } from "context/EventsContext";
// 2) Import the global modal
import GlobalModal from "components/GlobalModal";

/** Ancho del sidebar: se comparte con el padding izquierdo del contenido. */
const SIDEBAR_WIDTH = "xl:pl-[264px]";

export default function Admin() {
  const location = useLocation();
  // Initialize the state based on the current window width.
  const [open, setOpen] = React.useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth >= 1200;
    }
    return true; // fallback for server-side rendering
  });
  const [currentRoute, setCurrentRoute] = React.useState<RoutesType | null>(
    routes[0]
  );

  // Update sidebar state on window resize, and clean up the event listener.
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200) {
        setOpen(false);
      } else {
        setOpen(true);
      }
    };

    // Run once on mount.
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    getActiveRoute(routes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Finds which route's 'path' is included in the current pathname
  const getActiveRoute = (routes: RoutesType[]): void => {
    let activeRoute: RoutesType = routes[0];

    routes.forEach((r) => {
      // If user at "/", we see if r.path === "" => "Panel Principal"
      if (location.pathname === "/" && r.path === "") {
        activeRoute = r;
      }
      // Else if user at "/eventos", does the path include "eventos"?
      else if (r.path && r.path !== "" && location.pathname.includes(r.path)) {
        activeRoute = r;
      }
    });

    setCurrentRoute(activeRoute);
  };

  const getActiveNavbar = (routes: RoutesType[]): boolean => {
    // If you want to show a "secondary" style for certain routes
    // (like you had with 'secondary: true'), check that:
    let activeNavbar = false;

    routes.forEach((r) => {
      if (location.pathname === "/" && r.path === "" && r.secondary) {
        activeNavbar = true;
      } else if (
        r.path &&
        r.path !== "" &&
        location.pathname.includes(r.path) &&
        r.secondary
      ) {
        activeNavbar = true;
      }
    });

    return activeNavbar;
  };

  // Render a <Route> for each item in routes.tsx
  // If r.path === "", we use <Route index ...> => that becomes "/"
  // else <Route path="eventos" => "/eventos", etc.
  const getRoutes = (routes: RoutesType[]) => {
    return routes.map((prop, key) => {
      if (prop.name === "Iniciar Sesión") {
        // skip it, because /inicio-sesion is handled by AuthLayout
        return null;
      }
      if (prop.path === "") {
        // index route => matches "/"
        return <Route index element={prop.component} key={key} />;
      } else {
        return <Route path={prop.path} element={prop.component} key={key} />;
      }
    });
  };

  document.documentElement.dir = "ltr";

  return (
    <EventsProvider>
      <div className="flex min-h-screen w-full">
        {/* The collapsible sidebar */}
        <Sidebar open={open} onClose={() => setOpen(false)} />

        {/*
          El contenido reserva el ancho del sidebar sólo a partir de xl, que es
          donde el sidebar deja de superponerse. El max-width evita líneas de
          tabla larguísimas en monitores anchos.
        */}
        <div
          className={`app-surface flex min-h-screen w-full flex-col ${SIDEBAR_WIDTH}`}
        >
          <Navbar
            onOpenSidenav={() => setOpen(true)}
            brandText={currentRoute?.name ?? "Panel Principal"}
            secondary={getActiveNavbar(routes)}
          />

          <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 pb-12 md:px-6 lg:px-8">
            <PageHeader
              title={currentRoute?.name ?? "Panel Principal"}
              description={currentRoute?.description}
            />

            <Routes>
              {getRoutes(routes)}
              {/* fallback => if user goes to some unknown path, go home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>

          <Footer />
        </div>

        <GlobalModal />
      </div>
    </EventsProvider>
  );
}
